import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  language: string;
  videoType: "TechnicalVideo" | "UserManualVideo";
}

const VIDEO_URLS: Record<
  VideoModalProps["videoType"],
  Record<"Hindi" | "English", string>
> = {
  TechnicalVideo: {
    English: "https://www.youtube.com/embed/BqUOahWBhsc?autoplay=1",
    Hindi: "https://www.youtube.com/embed/8rwR08gotdA?autoplay=1",
  },
  UserManualVideo: {
    English: "https://www.youtube.com/embed/ppOfnLubYTg?autoplay=1",
    Hindi: "https://www.youtube.com/embed/s6M1PLn-nlc?autoplay=1",
  },
};

const VideoModal: React.FC<VideoModalProps> = ({
  open,
  onClose,
  language,
  videoType,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const selectedLanguage = language === "Hindi" ? "Hindi" : "English";
  const videoUrl = VIDEO_URLS[videoType][selectedLanguage];

  const handleClose = () => {
    onClose();
  };

  // Reset loading state when modal opens or language changes
  useEffect(() => {
    if (open) {
      setIsLoading(true);
    }
  }, [open, language, videoType]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          backgroundColor: "#F0F1F8",
          overflow: "visible",
          position: "relative",
          aspectRatio: "16 / 9",
          maxHeight: "80vh",
          margin: { xs: "0 15px", sm: "0 30px" },
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: -15,
          right: -15,
          color: "#ffffff",
          zIndex: 10,
          backgroundColor: "#3c9d50",
          width: "40px",
          height: "40px",
          "&:hover": {
            backgroundColor: "#2d873f",
          },
        }}
        aria-label="close"
      >
        <CloseIcon sx={{ fontSize: "20px" }} />
      </IconButton>

      <DialogContent
        sx={{
          padding: 0,
          position: "relative",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Loading Spinner */}
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress
              size={60}
              sx={{
                color: "#3c9d50",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Box
              sx={{
                color: "#666",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Loading video...
            </Box>
          </Box>
        )}

        <Box
          component="iframe"
          src={videoUrl}
          title={`${videoType === "TechnicalVideo" ? "Technical" : "User Manual"} ${selectedLanguage} Video`}
          sx={{
            width: "100%",
            height: "100%",
            border: 0,
            borderRadius: "12px",
            opacity: isLoading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={() => setIsLoading(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

export default VideoModal;
